const DEFAULT_API_BASE_URL = "http://localhost:18081";

export class ApiError extends Error {
  status: number;
  details: unknown;

  constructor(message: string, status: number, details: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export function getApiBaseUrl() {
  return (import.meta.env.VITE_API_BASE_URL ?? DEFAULT_API_BASE_URL).replace(/\/+$/, "");
}

function buildUrl(path: string) {
  return new URL(path, `${getApiBaseUrl()}/`).toString();
}

function parseErrorMessage(details: unknown, fallback: string) {
  if (!details || typeof details !== "object") return fallback;
  if ("message" in details && typeof details.message === "string" && details.message.trim()) {
    return details.message;
  }
  return fallback;
}

// Throttle "sem permissão" toasts so a single page that fires several admin
// requests doesn't stack a tower of toasts.
let lastForbiddenToastAt = 0;
function showForbiddenToast(message: string) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  if (now - lastForbiddenToastAt < 1500) return;
  lastForbiddenToastAt = now;
  // Lazy-load to avoid circular import with components that import this module.
  void import("sonner").then(({ toast }) => toast.error(message));
}

function isPasswordResetRequired(details: unknown): boolean {
  return !!details && typeof details === "object" && "error" in details
    && (details as { error?: unknown }).error === "password_reset_required";
}

function handleAuthFailure(status: number, message: string, path?: string, details?: unknown,
  silentForbidden?: boolean) {
  if (typeof window === "undefined") return;
  if (status !== 401 && status !== 403) return;

  // Redefinição obrigatória: o backend bloqueia a API até a troca. Leva o usuário à tela de troca em vez
  // de deslogar ou mostrar "sem permissão" (a própria tela de troca usa endpoints liberados pelo gate).
  if (status === 403 && isPasswordResetRequired(details)) {
    if (window.location.pathname !== "/redefinir-senha") {
      window.location.assign("/redefinir-senha");
    }
    return;
  }

  const publicPaths = new Set(["/login", "/signup"]);
  if (publicPaths.has(window.location.pathname)) return;

  // Lazy-import to avoid a circular dep between api.ts and the store.
  void import("@/store/useApp").then(({ useApp }) => {
    const account = useApp.getState().account;
    // 401 always means "you need to (re-)authenticate". 403 with no logged-in
    // account is effectively the same — Spring sometimes returns 403 for
    // anonymous requests, so treat that case as "session lost".
    if (status === 401 || !account) {
      useApp.getState().logout?.();
      window.location.assign("/login");
      return;
    }
    // Carga passiva de dados opcionais (ex.: opções de filtro): um 403 é ESPERADO quando o usuário não
    // tem a permissão daquele recurso — a tela já informa inline, então não emite toast nem resync.
    if (silentForbidden) return;
    // Authenticated but forbidden: a stale menu/button let the user attempt an
    // action their permissions no longer allow. Surface the error AND resync the
    // account from /auth/me so menus/routes/buttons self-correct without a manual
    // reload. Skip the resync when /auth/me itself 403s (avoids a refresh loop).
    showForbiddenToast(message || "Sem permissão para esta ação");
    if (!path || !path.includes("/auth/me")) {
      void useApp.getState().refreshAccount?.();
    }
  });
}

export async function requestJson<T>(
  path: string,
  init: Omit<RequestInit, "body"> & { body?: unknown; silentForbidden?: boolean } = {},
): Promise<T> {
  // silentForbidden é opção interna (não é RequestInit) — removida antes do fetch.
  const { silentForbidden, ...fetchInit } = init;
  const response = await fetch(buildUrl(path), {
    ...fetchInit,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = parseErrorMessage(details, "Nao foi possivel concluir a operacao");
    handleAuthFailure(response.status, message, path, details, silentForbidden);
    throw new ApiError(message, response.status, details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  return (await response.text()) as T;
}

export async function downloadFile(path: string, fallbackFilename: string): Promise<void> {
  const response = await fetch(buildUrl(path), {
    credentials: "include",
  });
  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = parseErrorMessage(details, "Nao foi possivel baixar o arquivo");
    handleAuthFailure(response.status, message, path, details);
    throw new ApiError(message, response.status, details);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  const filename = match ? decodeURIComponent(match[1]) : fallbackFilename;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Busca um arquivo (com credenciais) e abre em nova aba — para "visualizar" PDF sem forçar download. */
export async function viewFile(path: string): Promise<void> {
  const response = await fetch(buildUrl(path), { credentials: "include" });
  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = parseErrorMessage(details, "Nao foi possivel abrir o arquivo");
    handleAuthFailure(response.status, message, path, details);
    throw new ApiError(message, response.status, details);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function requestMultipart<T>(path: string, formData: FormData): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
    body: formData,
  });
  if (!response.ok) {
    let details: unknown = null;
    try {
      details = await response.json();
    } catch {
      details = await response.text().catch(() => null);
    }
    const message = parseErrorMessage(details, "Nao foi possivel concluir a operacao");
    handleAuthFailure(response.status, message, path, details);
    throw new ApiError(message, response.status, details);
  }
  if (response.status === 204) return undefined as T;
  const ct = response.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) return response.json() as Promise<T>;
  return (await response.text()) as T;
}

export function getApiErrorMessage(error: unknown, fallback = "Nao foi possivel concluir a operacao") {
  if (error instanceof ApiError) {
    return error.message;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}
